import { ChevronDown } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface Props<T extends string> {
  // Accessible name of the trigger and of the option list.
  label: string;
  // Trigger text while nothing is selected, e.g. "Status: All".
  allLabel: string;
  values: T[];
  options: Array<{ value: T; label: string }>;
  onChange: (values: T[]) => void;
  className?: string;
}

const summary = <T extends string>({
  label,
  allLabel,
  values,
  options,
}: Pick<Props<T>, "label" | "allLabel" | "values" | "options">) => {
  if (values.length === 0) return allLabel;
  if (values.length > 1) return `${label}: ${values.length} selected`;
  return `${label}: ${options.find(option => option.value === values[0])?.label ?? values[0]}`;
};

export const MultiSelectFilter = <T extends string>({
  label,
  allLabel,
  values,
  options,
  onChange,
  className,
}: Props<T>) => {
  const toggle = (value: T, checked: boolean) =>
    onChange(checked ? [...values, value] : values.filter(selected => selected !== value));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            "flex h-10 max-w-full items-center justify-between gap-2 rounded-md border border-[#ddd5cb] bg-white px-3 text-sm font-medium text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-desert-sand-300",
            values.length > 0 && "text-[#111b35]",
            className,
          )}
        >
          <span className="truncate">{summary({ label, allLabel, values, options })}</span>
          <ChevronDown className="size-4 shrink-0 text-gray-500" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        <div role="group" aria-label={label} className="max-h-72 overflow-y-auto">
          {options.map(option => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-[#f7f2ec]"
            >
              <input
                type="checkbox"
                value={option.value}
                className="size-4 accent-desert-sand-700"
                checked={values.includes(option.value)}
                onChange={event => toggle(option.value, event.target.checked)}
              />
              {option.label}
            </label>
          ))}
          {options.length === 0 ? <p className="px-2 py-1.5 text-sm text-gray-500">No options</p> : null}
        </div>
        {values.length > 0 ? (
          <button
            type="button"
            className="mt-1 w-full rounded border-t border-[#ebe5de] px-2 py-1.5 text-left text-sm font-semibold text-desert-sand-700 hover:bg-[#f7f2ec]"
            onClick={() => onChange([])}
          >
            Clear
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
